import React, {useState, useEffect} from 'react';
import {Grid, Typography} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import Card from '@material-ui/core/Card';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {FormType} from '../../PreQualification/types';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import {shouldShowHHIWarning, WarningMessage} from '@/Agent/Common/WarningMessage';

type WHLTabProps = {
    refParameters: Prequal;
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
    parameters: Quote;
    formType: FormType;
    parameterStore: ParameterStore;
};

const WHLTab = observer(({valueChanged, parameters, refParameters, formType, parameterStore}: WHLTabProps) => {
    const [error, setError] = useState({
        specifiedFaceAmountWinflex: '',
        deathBenefitChangeYear: '',
    });

    const rawHHI = parameterStore.prequal.incomeHouseholdText.value ?? '';
    const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;

    const specifiedPremiumAmount = Number(parameters.specifiedPremium?.value) || 0;
    const {exceeds: showPremiumWarning, threshold: premiumThreshold} = shouldShowHHIWarning({
        premium: specifiedPremiumAmount,
        annualHHI: hhi,
        premiumMode: 'Monthly',
    });

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const validatespecifiedFaceAmountWinflex = () => {
        const {healthRatingMOOWHL, specifiedFaceAmountWinflex} = parameters;

        if (['Standard Non-Tobacco', 'Standard Tobacco'].includes(healthRatingMOOWHL.value)) {
            setError((prevState) => ({
                ...prevState,
                specifiedFaceAmountWinflex:
                    +specifiedFaceAmountWinflex.value >= 50000 ? '' : 'specifiedFaceAmountWinflex >= 50000',
            }));
        }
    };

    const validateDeathBenefitChangeYear = () => {
        setError((prevState) => ({
            ...prevState,
            deathBenefitChangeYear:
                parameters.deathBenefitChangeYear.value < refParameters.age.value ? 'deathBenefitChangeYear > age' : '',
        }));
    };

    const currentState = refParameters.state.value;

    // Clear opposite field when switching solve mode
    useEffect(() => {
        if (parameters.productWHL.value === 'TAFEExpress') {
            if (parameters.solveForTAFE.value === 'Coverage') {
                // Clear Face Amount field
                if (parameters.specifiedFaceAmountTAFE.value) {
                    valueChanged(parameters.specifiedFaceAmountTAFE, '', formType);
                }
            } else if (parameters.solveForTAFE.value === 'Premium') {
                // Clear Premium field
                if (parameters.specifiedPremiumTAFE.value) {
                    valueChanged(parameters.specifiedPremiumTAFE, '', formType);
                }
            }
        }
    }, [parameters.productWHL.value, parameters.solveForTAFE.value]);

    useEffect(() => {
        const productValue = parameters.productWHL.value;
        const isTAFE = productValue === 'TAFEExpress';

        if (isTAFE && currentState === 'NY') {
            valueChanged(parameters.productWHL, 'MOOWholeLife', formType);
        }
    }, [currentState, parameters.productWHL.value, valueChanged, formType]);

    return (
        <>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Carrier Product</Typography>
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <SelectInputElement
                            param={{
                                label: 'Product',
                                name: 'productWHL',
                                options: fieldOptions.productWHL.options,
                                optionValues: fieldOptions.productWHL.optionValues,
                                ...parameters.productWHL,
                            }}
                            required={''}
                            valueChanged={valueChanged}
                            formType={formType}
                            isOptionDisabledCb={(option) => {
                                const optionValue = fieldOptions.productWHL.options.indexOf(option);
                                const actualValue = fieldOptions.productWHL.optionValues?.[optionValue];
                                return actualValue === 'TAFEExpress' && currentState === 'NY';
                            }}
                        />
                    </Grid>
                </Grid>
            </Card>
            {(parameters.moo.value || parameters.productWHL.value === 'TAFEExpress') && (
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Basics</Typography>
                    </div>
                    <Grid container spacing={3}>
                        {parameters.moo.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Risk Class',
                                        name: 'healthRatingMOOWHL',
                                        options: fieldOptions.healthRatingMOOWHL.options,
                                        optionValues: fieldOptions.healthRatingMOOWHL.optionValues,
                                        ...parameters.healthRatingMOOWHL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.productWHL.value === 'TAFEExpress' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Risk Class',
                                        name: 'healthRatingTAFE',
                                        options: fieldOptions.healthRatingTAFE.options,
                                        optionValues: fieldOptions.healthRatingTAFE.optionValues,
                                        ...parameters.healthRatingTAFE,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Card>
            )}
            <Card
                style={{
                    padding: 20,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium</Typography>
                </div>
                <Grid container spacing={3}>
                    {parameters.moo.value && parameters.easySolveOptionWHLMoo.value === 'Premium' && (
                        <Grid item xs={6}>
                            <TextBox
                                id={'specifiedFaceAmountWinflex'}
                                label={required('Specified Face Amount')}
                                value={parameters.specifiedFaceAmountWinflex?.value}
                                onChange={(value) => {
                                    valueChanged(parameters.specifiedFaceAmountWinflex, value, undefined);
                                    validatespecifiedFaceAmountWinflex();
                                }}
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                }}
                                error={!!error.specifiedFaceAmountWinflex}
                                helperText={error.specifiedFaceAmountWinflex}
                            />
                        </Grid>
                    )}
                    {parameters.moo.value &&
                        (parameters.easySolveOptionWHLMoo.value === 'Face' ||
                            parameters.easySolveOptionWHLMoo.value === 'No Solve') && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'specifiedPremium'}
                                    label={required('Specified Premium Amount')}
                                    value={parameters.specifiedPremium?.value}
                                    onChange={(value) => valueChanged(parameters.specifiedPremium, value, formType)}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                                {showPremiumWarning && <WarningMessage value={premiumThreshold} />}
                            </Grid>
                        )}
                    {parameters.productWHL.value === 'TAFEExpress' && (
                        <>
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForTAFE',
                                        options: fieldOptions.solveForTAFE.options,
                                        optionValues: fieldOptions.solveForTAFE.optionValues,
                                        ...parameters.solveForTAFE,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                            {parameters.solveForTAFE.value === 'Coverage' && (
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'specifiedPremiumTAFE'}
                                        label={required('Specified Premium Amount')}
                                        value={parameters.specifiedPremiumTAFE?.value}
                                        onChange={(value) =>
                                            valueChanged(parameters.specifiedPremiumTAFE, value, formType)
                                        }
                                        InputProps={{
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                </Grid>
                            )}
                            {parameters.solveForTAFE.value === 'Premium' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Face Amount',
                                            name: 'specifiedFaceAmountTAFE',
                                            options: fieldOptions.specifiedFaceAmountTAFE.options,
                                            optionValues: fieldOptions.specifiedFaceAmountTAFE.optionValues,
                                            ...parameters.specifiedFaceAmountTAFE,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Payment Frequency',
                                        name: 'premiumFrequencyTAFE',
                                        options: fieldOptions.premiumFrequencyTAFE.options,
                                        optionValues: fieldOptions.premiumFrequencyTAFE.optionValues,
                                        ...parameters.premiumFrequencyTAFE,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        </>
                    )}
                    {parameters.productWHL.value !== 'TAFEExpress' && (
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Premium Payment Frequency',
                                    name: 'premiumFrequency',
                                    options: fieldOptions.premiumFrequency.options,
                                    optionValues: fieldOptions.premiumFrequency.optionValues,
                                    ...parameters.premiumFrequency,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                    )}
                </Grid>
            </Card>
            {parameters.moo.value && (
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium Details</Typography>
                    </div>
                    <Grid container spacing={3}>
                        {parameters.moo.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForWHLMoo',
                                        options: fieldOptions.solveForWHLMoo.options,
                                        optionValues: fieldOptions.solveForWHLMoo.optionValues,
                                        ...parameters.solveForWHLMoo,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.moo.value && parameters.solveForWHLMoo.value == 'EasySolve' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Easy Solve Option',
                                        name: 'easySolveOptionWHLMoo',
                                        options: fieldOptions.easySolveOptionWHLMoo.options,
                                        optionValues: fieldOptions.easySolveOptionWHLMoo.optionValues,
                                        ...parameters.easySolveOptionWHLMoo,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.complianceTest.value !== 'CashValueAccumulationTest' && (
                            <Grid item xs={4}>
                                <SwitchCustom
                                    param={{
                                        name: 'mecAvoidance',
                                        label: 'MEC Avoidance',
                                        ...parameters.mecAvoidance,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Death Benefit Option',
                                    name: 'deathBenefitOption',
                                    options: fieldOptions.deathBenefitOption.options,
                                    optionValues: fieldOptions.deathBenefitOption.optionValues,
                                    ...parameters.deathBenefitOption,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                        {refParameters.age.value < 70 && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'deathBenefitChangeYear'}
                                    label={'Death Benefit Change Age'}
                                    value={parameters.deathBenefitChangeYear?.value}
                                    onChange={(value) => {
                                        valueChanged(parameters.deathBenefitChangeYear, value, formType);
                                        validateDeathBenefitChangeYear();
                                    }}
                                    type="number"
                                    error={!!error.deathBenefitChangeYear}
                                    helperText={error.deathBenefitChangeYear}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Card>
            )}
            {parameters.moo.value && (
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Cashflow</Typography>
                    </div>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <SwitchCustom
                                param={{
                                    name: 'disbursement',
                                    label: 'Income',
                                    ...parameters.disbursement,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                        {parameters.disbursement.value && (
                            <>
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Income Option',
                                            name: 'incomeOption',
                                            options: fieldOptions.incomeOption.options,
                                            optionValues: fieldOptions.incomeOption.optionValues,
                                            ...parameters.incomeOption,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'incomeStartAge'}
                                        label={'Income Start Age'}
                                        value={parameters.incomeStartAge?.value}
                                        onChange={(value) => valueChanged(parameters.incomeStartAge, value, formType)}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'incomeEndAge'}
                                        label={'Income End Age'}
                                        value={parameters.incomeEndAge?.value}
                                        onChange={(value) => valueChanged(parameters.incomeEndAge, value, formType)}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'annualIncomeAmount'}
                                        label={'Annual Income Amount'}
                                        value={parameters.annualIncomeAmount?.value}
                                        onChange={(value) =>
                                            valueChanged(parameters.annualIncomeAmount, value, formType)
                                        }
                                        InputProps={{
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <SwitchCustom
                                        param={{
                                            name: 'overloanProtection',
                                            label: 'Overloan Protection',
                                            ...parameters.overloanProtection,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Card>
            )}
            {parameters.moo.value && (
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Policy rider</Typography>
                    </div>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <SwitchCustom
                                param={{
                                    name: 'acceleratedBenefit',
                                    label: 'Accelerated Death Benefit',
                                    ...parameters.acceleratedBenefit,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <SwitchCustom
                                param={{
                                    name: 'DeathBenefitProtection',
                                    label: 'Death Benefit Protection',
                                    ...parameters.DeathBenefitProtection,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                    </Grid>
                </Card>
            )}
        </>
    );
});

export default WHLTab;
